import {
    Component, ViewChild, OnInit, Input, Output, EventEmitter, OnChanges,
    SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

import _values from 'lodash-es/values';

import { CardFormComponent, IOptionsCardForm } from 'libraries/module/formbuilder/forms/card-form/card-form.component';
import { CARD_OPTION_LABELS } from '../../config/wording';
import { dateRangeFormGroup } from '../config/form';
import { ProcessingSearchParameters } from '../../models/ProcessingSearchParameters';
import { IMultiselectOptions } from 'libraries/module/formbuilder/fields/multiselect/multiselect.model';
import { IDateRangeFieldSpecs } from 'libraries/module/formbuilder/fields/date-range/date-range.component';
import { IFieldSpecs, IDateFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { HelperService } from 'app_sasha/shared/services/helper.service';
import { IsBefore } from 'libraries/module/formbuilder/forms/utils/validators/isBefore.validator';
import { IsPast } from 'libraries/module/formbuilder/forms/utils/validators/isPast.validator';
import { IsAfter } from 'libraries/module/formbuilder/forms/utils/validators/isAfter.validator';
import { FIELD_LABELS, TEMPLATE_LABELS } from '../../config/processing-wording';
import { SearchUtils } from '../../models/SearchUtils';
import { PROCESSING_STATUSES } from 'app_sasha/shared/wordings/processing';
import { ProcessingType, PROCESSING_WORDING } from 'libraries/service/processing/processing.service';
import { ProcessingStatus } from 'app_sasha/shared/resources/processings/ProcessingStatus';
import { SearchUtilsService } from "../../models/search-utils.service";

@Component({
    selector: 'processing-search-form',
    template: `
        <card-form #cardForm
                [options]="cardOptions"
                (onSubmit)="submit()">
            <div class="card-header-content">
                <h1>{{ templateLabels.treatmentSearch }}</h1>
            </div>
            <div class="btn-right">
                <btnForm styleType="actionHeaderLink"
                        (click)="resetForm()">
                    {{ templateLabels.newSearch }}
                </btnForm>
            </div>
            <div class="card-body-content card-padding-inside">
                <div class="col-to-row-s-tb">
                    <multiselect #typeField
                                *ngIf="typeOptions?.length"
                                class="flex-1 sep-right"
                                [fieldConfig]="fieldConfig.type"
                                [options]="typeOptions">
                    </multiselect>
                    <multiselect *ngIf="statusOptions?.length" class="flex-1"
                                [fieldConfig]="fieldConfig.status"
                                [options]="statusOptions">
                    </multiselect>
                </div>
                <div class="col-to-row-s-tb">
                    <date-range-field class="flex-1"
                                    [formGroup]="form.get('whenCreated')"
                                    [fieldConfig]="fieldConfig.whenCreated">
                    </date-range-field>
                </div>
            </div>
        </card-form>
    `,
})
export class ProcessingSearchFormComponent implements OnInit, OnChanges {
    private static readonly DATE_FIELDS: string[] = ['whenCreated'];
    private static readonly ARRAY_FIELDS: string[] = ['type', 'status'];

    @Input()
    public searchParameters: ProcessingSearchParameters;

    @Output()
    public readonly onSubmit: EventEmitter<ProcessingSearchParameters> = new EventEmitter();

    @Output()
    public readonly onReset: EventEmitter<null> = new EventEmitter();

    //////////////////////////////////// COMPONENT VARIABLES ////////////////////////////////////

    @ViewChild('cardForm')
    public cardForm: CardFormComponent;

    public form: FormGroup;
    public statusOptions: IMultiselectOptions[];
    public typeOptions: IMultiselectOptions[];
    public templateLabels: { [key: string]: string } = TEMPLATE_LABELS;

    public fieldConfig: {
        type: IFieldSpecs | IDateRangeFieldSpecs,
        status: IFieldSpecs,
        whenCreated: IDateRangeFieldSpecs
    };

    public cardOptions: IOptionsCardForm = {
        formGroup: null,
        labels: {
            error: CARD_OPTION_LABELS.error,
            submit: CARD_OPTION_LABELS.submit,
        },
    };

    constructor(
        private readonly fb: FormBuilder,
        private readonly helperService: HelperService,
        private readonly searchUtilsService: SearchUtilsService,
    ) { }

    public ngOnInit(): void {
        this.form = this.fb.group({
            whenCreated: dateRangeFormGroup(this.fb),
            status: [[]],
            type: [[]],
        });

        this.cardOptions.formGroup = this.form;

        //get Status field options
        const statusValues: string[] = _values(ProcessingStatus);
        this.statusOptions =
            this.helperService.getMultiselectOptions(PROCESSING_STATUSES, statusValues, true);

        //get Type field options
        const typeValues: string[] = _values(ProcessingType).filter((type: string) => {
            return ['sendEmail', 'sendSms', 'qualityCheck', 'issue', 'cancel', 'void', 'synchronize'].includes(type);
        });
        this.typeOptions =
            this.helperService.getMultiselectOptions(PROCESSING_WORDING, typeValues, true);

        //form config
        const whenCreatedMin: IDateFieldSpecs = {
            label: FIELD_LABELS.whenCreatedMin,
            defaultDate: new Date(),
            formGroup: this.form.get('whenCreated') as FormGroup,
            formControlName: 'min',
            icon: 'fa fa-calendar',
            maxDate: new Date(),
            validators: [
                new IsBefore(this.form.get(['whenCreated', 'max'])),
                new IsPast(),
            ]
        };
        const whenCreatedMax: IDateFieldSpecs = {
            label: FIELD_LABELS.whenCreatedMax,
            defaultDate: new Date(),
            formGroup: this.form.get('whenCreated') as FormGroup,
            formControlName: 'max',
            icon: 'fa fa-calendar',
            maxDate: new Date(),
            validators: [
                new IsAfter(this.form.get(['whenCreated', 'min'])),
                new IsPast(),
            ]
        };

        const whenCreated: IDateRangeFieldSpecs = {
            min: whenCreatedMin,
            max: whenCreatedMax,
        };
        this.fieldConfig = {
            status: this.helperService.getSimpleFieldOptions('status', this.form, FIELD_LABELS),
            type: this.helperService.getSimpleFieldOptions('type', this.form, FIELD_LABELS),
            whenCreated
        };

        this.updateForm();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes.searchParameters.firstChange
            && changes.searchParameters.currentValue !== changes.searchParameters.previousValue) {
            this.updateForm();
        }
    }

    public submit(): void {
        if (this.searchParameters) {
            this.searchParameters = SearchUtils.populateSearchParametersFromForm<ProcessingSearchParameters>(
                this.form,
                this.searchParameters
            );
        } else {
            this.searchParameters = SearchUtils.populateSearchParametersFromForm<ProcessingSearchParameters>(
                this.form,
                new ProcessingSearchParameters()
            );
        }

        this.searchParameters.first = 1;

        this.onSubmit.emit(this.searchParameters);
    }

    public resetForm(): void {
        this.form.reset();
        this.onReset.emit();
    }

    private updateForm(): void {
        this.searchUtilsService.patchAllValues(
            this.form,
            this.searchParameters,
            {
                date: ProcessingSearchFormComponent.DATE_FIELDS,
                array: ProcessingSearchFormComponent.ARRAY_FIELDS,
            }
        );
    }
}
